import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FitWidth } from "@/components/FitWidth";
import { partOf } from "@/lib/spot";
import { ReceiptPaper } from "@/components/ReceiptPaper";
import { ShareActions } from "@/components/ShareActions";
import { FamilyTimeline } from "@/components/FamilyTimeline";
import { ReceiptStatusBar } from "@/components/ReceiptStatusBar";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/hooks";
import { useAuth } from "@/lib/auth";
import { PaymentChecks } from "@/components/PaymentVerify";

export default function ReceiptView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: settings } = useSettings();
  const { isAccountant } = useAuth();
  const { data, isError } = useQuery({ queryKey: ["receipt", id], queryFn: () => api.get(`/receipts/${id}`).then((r) => r.data) });

  if (isError) return <div className="text-slate-600" data-testid="receipt-not-found">Receipt not found.</div>;
  if (!data) return null;
  return (
    <div>
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => nav(isAccountant ? "/" : "/history")} data-testid="back-to-history-btn"><ArrowLeft className="mr-2 h-4 w-4" />{isAccountant ? "Payments" : "History"}</Button>
        <div className="flex flex-wrap gap-2">
          {!isAccountant && (
            <>
              <FamilyTimeline size="default" query={{ contact: data.inputs.contact || "", email: data.inputs.email || "", childName: data.inputs.childName || "", parentName: data.inputs.parentName || "" }} />
              <ShareActions inputs={data.inputs} c={data.computed} receiptNo={data.receipt_no} receiptId={id} schoolName={settings?.school_name} />
              <Button variant="outline" onClick={() => nav(`/?edit=${id}`)} data-testid="edit-receipt-btn"><Pencil className="mr-2 h-4 w-4" />Edit</Button>
            </>
          )}
          <Button onClick={() => window.print()} data-testid="view-print-btn" className="bg-plum-700 hover:bg-plum-800"><Printer className="mr-2 h-4 w-4" />Print / PDF</Button>
        </div>
      </div>
      {!isAccountant && <ReceiptStatusBar id={id} />}
      <PaymentChecks r={data} />
      <div className="grid-bg rounded-lg border border-plum-900/10 p-3 sm:p-8">
        <FitWidth>
          <ReceiptPaper inputs={data.inputs} c={data.computed} settings={settings} receiptNo={data.receipt_no} payment={data.status === "admitted" ? data.payment : null} formFee={data.form_fee} part={partOf(data)}
            signatureUrl={`${process.env.REACT_APP_BACKEND_URL}/api/receipts/${id}/signature`} />
        </FitWidth>
      </div>
    </div>
  );
}
